import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowUpRight, Calendar, User, Tag } from "lucide-react";
import { loadBlogPost, loadBlogList } from "@/lib/content";
import { blogPostingSchema, breadcrumbSchema } from "@/lib/schema";
import useSEO from "@/hooks/useSEO";

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found
  const [related, setRelated] = useState([]);

  useEffect(() => {
    loadBlogPost(slug).then((p) => setPost(p || null));
    loadBlogList().then((list) => setRelated(list.filter((x) => x.slug !== slug).slice(0, 3)));
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [slug]);

  useSEO({
    title: post ? `${post.title} | ElectroMax Blog` : "Article | ElectroMax",
    description: post?.excerpt || "ElectroMax field notes.",
    image: post?.cover,
    type: "article",
    jsonld: post
      ? [
          blogPostingSchema(post),
          breadcrumbSchema([
            { name: "Home", href: "/" },
            { name: "Blog", href: "/blog" },
            { name: post.title, href: `/blog/${post.slug}` },
          ]),
        ]
      : undefined,
  });

  if (post === undefined) {
    return <div className="pt-40 pb-32 text-white/60 mx-auto max-w-[900px] px-6" data-testid="blog-post-loading">Loading…</div>;
  }

  if (post === null) {
    return (
      <section className="pt-40 pb-32 min-h-[70vh]" data-testid="blog-post-not-found">
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-3">Post not found</div>
          <h1 className="font-display text-5xl font-black tracking-tight">We haven&rsquo;t written this one yet.</h1>
          <Link to="/blog" className="inline-flex mt-8 items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-3">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="blog-post-page" className="bg-[#0A0A0A] text-white">
      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-14 overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] bg-[#E60000]/10 blur-[140px] rounded-full" />
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-white/60 hover:text-white mb-8">
            <ArrowLeft className="w-3 h-3" /> All articles
          </Link>
          <div className="flex flex-wrap items-center gap-4 text-[10px] tracking-[0.24em] uppercase text-white/50 mb-6">
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtDate(post.date)}</span>
            <span className="inline-flex items-center gap-1.5"><User className="w-3 h-3" /> {post.author}</span>
            {post.tags?.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-[#E60000] font-bold">
                <Tag className="w-3 h-3" /> {t}
              </span>
            ))}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl lg:text-[72px] leading-[0.98] tracking-tighter font-black"
          >
            {post.title}
          </motion.h1>
          {post.excerpt && (
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-3xl">
              {post.excerpt}
            </p>
          )}
        </div>

        {post.cover && (
          <div className="mt-14 mx-auto max-w-[1200px] px-6 md:px-10">
            <div className="border border-[#262626] overflow-hidden">
              <img src={post.cover} alt={`${post.title} — ${post.tags?.[0] || "Field Notes"} · ElectroMax Systems`} loading="eager" fetchPriority="high" className="w-full h-[300px] md:h-[520px] object-cover" />
            </div>
          </div>
        )}
      </section>

      {/* Body */}
      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-[760px] px-6 md:px-10">
          <article
            data-testid="blog-post-body"
            className="prose-blog"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
          </article>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-[#262626] py-20 bg-[#0F0F0F]">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="text-xs tracking-[0.24em] uppercase text-white/50 mb-3">— Keep Reading</div>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tighter">More field notes.</h2>
              </div>
              <Link to="/blog" className="hidden md:inline-flex items-center gap-2 text-[#E60000] font-bold text-sm">
                All articles <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4 md:gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`related-post-${r.slug}`}
                  className="group border border-[#262626] hover:border-[#E60000] transition-colors overflow-hidden"
                >
                  {r.cover && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={r.cover} alt={`Related article — ${r.title}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-2">{fmtDate(r.date)}</div>
                    <div className="font-display text-lg font-bold tracking-tight leading-tight">{r.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
